import React, { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { Mic, Square, Loader2, AlertCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { Progress } from "./ui/progress";

const UPLOAD_TIMEOUT = 120000; // 2 minutes in milliseconds
const MAX_RECORDING_TIME = 300000; // 5 minutes in milliseconds

const SpeechToTextInput = ({ onTranscriptionComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [progress, setProgress] = useState(0);

  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const recordingInterval = useRef(null);
  const recordingTimeout = useRef(null);

  // Cleanup function
  useEffect(() => {
    return () => {
      stopAllMediaTracks();
      clearAllTimers();
    };
  }, []);

  const clearAllTimers = () => {
    if (recordingInterval.current) clearInterval(recordingInterval.current);
    if (recordingTimeout.current) clearTimeout(recordingTimeout.current);
  };

  const stopAllMediaTracks = () => {
    if (mediaRecorder.current && mediaRecorder.current.stream) {
      mediaRecorder.current.stream.getTracks().forEach((track) => track.stop());
    }
  };

  const checkBrowserCompatibility = () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError(
        "Váš prehliadač nepodporuje nahrávanie zvuku. Prosím, skúste iný prehliadač."
      );
      return false;
    }
    return true;
  };

  const startRecording = async (e) => {
    e.preventDefault();
    setError(null);

    if (!checkBrowserCompatibility()) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          noiseSuppression: true,
          echoCancellation: true,
        },
      });

      mediaRecorder.current = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });

      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = async () => {
        stopAllMediaTracks();
        await processAudio(
          new Blob(audioChunks.current, { type: "audio/webm" })
        );
      };

      // Start recording
      mediaRecorder.current.start(250);
      setIsRecording(true);
      setRecordingTime(0);

      // Set up recording time tracking
      recordingInterval.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1000);
      }, 1000);

      // Set up maximum recording time limit
      recordingTimeout.current = setTimeout(() => {
        if (isRecording) {
          stopRecording(new Event("timeout"));
          setError("Maximálny čas nahrávania (5 minút) bol dosiahnutý.");
        }
      }, MAX_RECORDING_TIME);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      setError(
        err.name === "NotAllowedError"
          ? "Prístup k mikrofónu bol zamietnutý. Prosím, povoľte prístup k mikrofónu."
          : "Nepodarilo sa pristúpiť k mikrofónu. Prosím, skontrolujte pripojenie."
      );
    }
  };

  const stopRecording = (e) => {
    e.preventDefault();
    if (mediaRecorder.current && isRecording) {
      clearAllTimers();
      mediaRecorder.current.stop();
      setIsRecording(false);
      setProgress(0);
    }
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT);

    try {
      const formData = new FormData();
      formData.append(
        "file",
        new File([audioBlob], "recording.webm", {
          type: "audio/webm",
          lastModified: Date.now(),
        })
      );

      const uploadStartTime = Date.now();

      const response = await fetch("/api/transcribe", {
        method: "POST",
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Nepodarilo sa previesť zvuk na text");
      }

      const data = await response.json();

      if (!data.transcription) {
        throw new Error("Prázdny prepis");
      }

      onTranscriptionComplete(data.transcription);

      // Calculate and log processing time
      const processingTime = Date.now() - uploadStartTime;
      console.log(`Audio processing completed in ${processingTime}ms`);
    } catch (err) {
      console.error("Audio processing error:", err);
      if (err.name === "AbortError") {
        setError(
          "Požiadavka vypršala. Prosím, skúste to znova s kratšou nahrávkou."
        );
      } else {
        setError(
          err.message ||
            "Nepodarilo sa spracovať zvuk. Prosím, skúste to znova."
        );
      }
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${(seconds % 60).toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="flex items-center space-x-4">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={isRecording ? stopRecording : startRecording}
                variant={isRecording ? "destructive" : "outline"}
                disabled={isProcessing}
                className="w-12 h-12 rounded-full p-0 relative"
                type="button"
              >
                {isProcessing ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : isRecording ? (
                  <Square className="h-6 w-6" />
                ) : (
                  <Mic className="h-6 w-6" />
                )}
                {isRecording && (
                  <span className="absolute -top-1 -right-1 w-3 h-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isRecording ? "Zastaviť nahrávanie" : "Začať nahrávať"}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {isRecording && (
          <div className="flex items-center space-x-2">
            <div className="text-sm font-medium text-red-600">
              {formatTime(recordingTime)}
            </div>
            <Progress
              value={(recordingTime / MAX_RECORDING_TIME) * 100}
              className="w-24"
            />
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isProcessing && (
        <div className="text-sm text-gray-500 animate-pulse">
          Prevádzam zvuk na text...
        </div>
      )}
    </div>
  );
};

export default SpeechToTextInput;
